import { ComponentProps, type ReactNode, useState } from 'react';
import { Trans } from '@lingui/react';
import { Button } from '@startuptools/ui/Button';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { Stack } from '@startuptools/ui/Stack';
import { isPromise } from '../../helpers/is-promise';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import { ApolloError } from '@apollo/client';
import { Alert } from '@startuptools/ui/joy/Alert';
import { isNil } from 'lodash-es';
import { DialogTitle } from '@startuptools/ui/joy/DialogTitle';
import { DialogContent } from '@startuptools/ui/joy/DialogContent';
import { DialogActions } from '@startuptools/ui/joy/DialogActions';

type KnownError = 'HAS_STRIPE_INVOICE';

const errorMessages: Record<KnownError, React.ReactNode> = {
  HAS_STRIPE_INVOICE: (
    <Trans id="This program has an open or paid invoice connected to it. Contact support if you want to delete this program." />
  ),
};

export const AlertConfirmDialogContent = ({
  title = <Trans id="Confirmation" />,
  content = <Trans id="Are you sure you want to proceed?" />,
  confirmButtonText = <Trans id="Confirm" />,
  cancelButtonText = <Trans id="Cancel" />,
  onConfirm,
  onClose,
  loading,
}: {
  title?: ReactNode;
  content?: ReactNode;
  confirmButtonText?: ReactNode;
  cancelButtonText?: ReactNode;
  onConfirm: () => Promise<unknown> | unknown;
  onClose: () => unknown;
  loading?: boolean;
}) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<'HAS_STRIPE_INVOICE' | undefined>(undefined);

  const onConfirmHandler = async () => {
    const res = onConfirm();
    if (isPromise(res)) {
      /* if the loading prop is provided then we don't handle loading state here */
      if (isNil(loading)) setIsLoading(true);
      try {
        await res;
        return onClose();
      } catch (e) {
        if (e instanceof ApolloError && e.message === 'HAS_STRIPE_INVOICE') {
          setError(e.message);
          return;
        }
        genericErrorToast();
      } finally {
        setIsLoading(false);
      }
    } else {
      onClose();
    }
  };

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{error ? <Alert color="danger">{errorMessages[error]}</Alert> : content}</DialogContent>

      <DialogActions orientation="horizontal-reverse">
        <Button
          color={isChecked ? 'neutral' : 'primary'}
          variant={isChecked ? 'plain' : 'solid'}
          disabled={isLoading || Boolean(loading)}
          onClick={onClose}
        >
          {cancelButtonText}
        </Button>
        <Stack direction="row" gap={2} justifyContent="end" alignItems="center">
          <Checkbox
            label={<Trans id="Yes, I'm sure" />}
            checked={isChecked}
            onChange={() => setIsChecked(!isChecked)}
            disabled={isLoading || Boolean(loading)}
          />
          <Button
            variant="solid"
            color="danger"
            disabled={!isChecked || Boolean(error)}
            loading={isLoading || Boolean(loading)}
            onClick={onConfirmHandler}
          >
            {confirmButtonText}
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
};

export const AlertConfirmDialog = ({
  onClose,
  sx,
  ...props
}: {
  sx?: ComponentProps<typeof ModalDialog>['sx'];
} & ComponentProps<typeof AlertConfirmDialogContent>) => {
  return (
    <Modal open onClose={onClose}>
      <ModalDialog sx={{ width: '400px', ...sx }}>
        <AlertConfirmDialogContent {...props} onClose={onClose} />
      </ModalDialog>
    </Modal>
  );
};
